import {ScreenComponent} from "./ScreenComponent.js";
import {div, processDescendents} from '@megaphonenpm/mputils/src/DOM.js';
import {notEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {MegaScreen} from "../MegaScreen.js";
import {MediaContainer} from "../MediaContainer.js";
import {play} from "../Animation.js";
import {get} from 'lodash-es';

/**
 * Displays an animation for the specified users' selected answers.
 */
export class LiveReactionsComponent extends ScreenComponent {
  constructor (themeID) {
    super(themeID, [], { visibleProducts: ['livereactions'],
      maxVisibleReactions: 30,
      reactions: [],
      availableReactions: []
    });
  }

  build () {
    super.build();

    this.view = div('liveReactionsContainer', 'liveReactionsContainer');
    this.view.setAttribute('data-no-scene-animations', 'true');
    // Indicate the states and products during which the view should be visible
    this.view.classList.add('state_scene', 'state_scene_result');
    this.visibleProducts.forEach(productName => {
      this.view.classList.add(`product_${productName}`);
    });
    this.view.style.position = 'relative';

    // Pre-create the max number of Live Reactions allowed on screen at any one time. Reactions
    // are reused after their animation completes. Pre-creating the reactions reduces the impact
    // of creating new reactions every time a new vote arrives.
    for (let i = 0; i < this.maxVisibleReactions; i++) {
      let thisReaction = this.createLiveReaction();
      this.reactions.push(thisReaction);
      this.availableReactions.push(thisReaction);
    }
  }

  createLiveReaction () {
    let reaction = new MediaContainer('reactionMedia');
    // Opt out of scene animations for the reaction element and its descendents. This prevents unwanted
    // DOM-reflow triggers for reaction elements.
    processDescendents(reaction.view, node => {
      node.setAttribute('data-no-scene-animations', 'true');
    });
    reaction.view.setAttribute('data-no-scene-animations', 'true');
    reaction.view.style.height = '100%';
    reaction.view.style.position = 'absolute';
    reaction.view.style.transform = 'translate3d(0px, 0px, 0px)';
    reaction.view.style.backfaceVisibility = 'hidden';
    reaction.view.style.transformStyle = 'preserve-3d';
    reaction.view.style.animationDuration = '3s';
    reaction.view.style.animationTimingFunction = 'linear';
    reaction.view.style.animationFillMode = 'both';
    reaction.view.style.visibility = 'hidden';
    reaction.view.addEventListener('animationend', () => {
      // This reaction's animation is complete, so return the reaction to the pool
      // of available reactions.
      this.availableReactions.push(reaction);
      reaction.view.style.visibility = 'hidden';
      reaction.view.style.removeProperty('will-change');
    });


    this.view.append(reaction.view);

    return reaction;
  }

  setSize (width, height) {
    this.width = width;
    this.height = height;
    // Set size of reactions container
    this.view.style.width = `${width}px`;
    this.view.style.height = `${height}px`;
    this.view.style.minWidth = `${width}px`;

    // Set size of pre-built reactions
    this.reactions.forEach(reaction => {
      reaction.view.style.width = `${height}px`;
      reaction.view.style.height = `${height}px`;
    });
  }

  setOffset (x, y) {
    this.view.style.marginLeft = `${x}px`;
    this.view.style.marginTop = `${-this.height + y}px`;
  }

  setRecentResponses (responses) {
    let numReactionsPerResponse = get(MegaScreen.globals.dataStore.deploymentSettings, 'defaults.presentation.screen.num_reactions_per_response',1);
    responses.forEach((response, index) => {
      for (let i = 0; i < numReactionsPerResponse; i++) {
        let thisReaction;
        if (this.availableReactions.length > 0) {
          thisReaction = this.availableReactions.pop();
          thisReaction.view.style.willChange = 'transform, opacity';
          let mediaURI = get(MegaScreen.globals.appState, `scene.content.answers[${response.response-1}].media.screen.uri`);
          if (notEmpty(mediaURI)) {
            thisReaction.setURI(mediaURI);
          }
          thisReaction.view.style.visibility = 'visible';
          play(thisReaction.view, 'horizontal-livereaction', Math.floor((index * 100)+(50+Math.random()*500)));
        }
      }
    });
  }

  reset () {
    // Changes the horizontal distance of the reaction animation for the current scene. Each theme overwrites this
    // width at the beginning of each scene. Because all themes share the single 'reaction-container-width' variable,
    // this implementation prevents multiple themes from displaying the same scene at the same time. If MegaScreen
    // ever needs to display multiple themes simultaneously, each theme will need its own @keyframes animation.
    document.documentElement.style.setProperty('--reaction-container-width', `${this.width - this.height}px`);
    this.reactions.forEach(reaction => {
      reaction.view.style.visibility = 'hidden';
    });
  }

  playIntro () {
    // Fade in the live reactions bar at the same time as the main scene/state animation
    let delay = this.getThemeSetting('design.default.screen.animation.main.start.delay');
    play(this.view, 'fade-in', delay);
  }
}